import { DigitByDigitAnimation } from '@/components/atomic/number-loading-animation'
import Text, { BodyXS, FontWeight, TextVariant } from '@/components/atomic/text'
import { DifferenceIndicator } from '@/components/molecules/difference-indicator'
import {
  LOADING_ANIMATION_TIMING,
  LoadingAnimationType,
} from '@/shared/constants/animations'
import {
  type LoadingState,
  shouldShowLoadingAnimation,
} from '@/shared/helpers/loading/stat-cards-and-charts'
import { PLACEHOLDER } from '@/utils/presentation/formatting'

import { StatCardContainer, StatCardLabel, StatCardValue } from './styles'

interface StatCardProps {
  className?: string
  label?: string
  value?: string | number | React.ReactNode
  loadingState?: LoadingState
}

export function StatCard({
  className,
  label,
  value,
  loadingState,
}: StatCardProps) {
  const showDigitAnimation =
    shouldShowLoadingAnimation(loadingState, LoadingAnimationType.DIGIT) &&
    loadingState?.animationType === LoadingAnimationType.DIGIT
  return (
    <StatCardContainer className={className}>
      <StatCardLabel>
        <BodyXS className={loadingState?.isLoading ? 'mb-1' : ''}>
          {label}
        </BodyXS>
      </StatCardLabel>
      <StatCardValue>
        {showDigitAnimation && loadingState && (
          <DigitByDigitAnimation
            type={loadingState.dataType}
            digitChangeInterval={
              LOADING_ANIMATION_TIMING.STAT_CARD_DIGIT_INTERVAL
            }
            duration={
              loadingState.duration ??
              LOADING_ANIMATION_TIMING.STAT_CARD_DURATION
            }
            className="h-5"
          />
        )}
        {!showDigitAnimation && (
          // biome-ignore lint/complexity/noUselessFragments: Bulk suppression as part of biome migration
          <>
            {typeof value === 'string' || typeof value === 'number' ? (
              <Text
                variant={TextVariant.BodySubtext}
                fontWeight={FontWeight.Medium}
                skeletonWidth={loadingState?.skeletonWidth}
                isLoading={loadingState?.isLoading ?? false}
                className="h-5"
              >
                {value ?? PLACEHOLDER}
              </Text>
            ) : (
              <div className="flex flex-col">{value ?? PLACEHOLDER}</div>
            )}
          </>
        )}
        <div className="mt-1 h-4" />
      </StatCardValue>
    </StatCardContainer>
  )
}

interface StatCardWithDeltaProps {
  className?: string
  label?: string
  value?: string | number
  delta?: number
  deltaFormatter?: (value: number) => string
  isLoading?: boolean
  skeletonWidth?: string
}

export function StatCardWithDelta({
  className,
  label,
  value,
  delta,
  deltaFormatter,
  isLoading,
  skeletonWidth = '50%',
}: StatCardWithDeltaProps) {
  return (
    <StatCardContainer className={className}>
      <StatCardLabel>
        <BodyXS className={isLoading ? 'mb-1' : ''}>{label}</BodyXS>
      </StatCardLabel>
      <StatCardValue>
        <Text
          variant={TextVariant.BodySubtext}
          fontWeight={FontWeight.Medium}
          isLoading={isLoading}
          skeletonWidth={skeletonWidth}
        >
          {value ?? PLACEHOLDER}
        </Text>
        {delta !== undefined && delta !== null && !isLoading && (
          <div className="mt-1">
            <DifferenceIndicator
              difference={delta}
              formatter={deltaFormatter}
              className="text-xs"
            />
          </div>
        )}
      </StatCardValue>
    </StatCardContainer>
  )
}
